import type { LayerWithType } from 'hooks/internalLayer/types';
import { useDeleteInternalLayerMutation } from 'hooks/internalLayer/useDeleteInternalLayerMutation';
import { useDeleteExternalLayerMutation } from 'hooks/externalLayer/useDeleteExternalLayerMutation';
import { useHubChromeEnabled } from 'utils/featureFlags';
import {
  SitStatExternalLayerItem,
  SitStatInternalLayerItem,
} from '../SitStatTab/SitStatLayerItems';
import { ExternalLayerItem } from './ExternalLayerItem';
import { InternalLayerItem } from './InternalLayerItem';
import { useUscgMapContext } from './UscgMapContext';

export type LayerListItemTourAttrs = {
  dataTour?: string;
  zoomTour?: string;
  qualityTour?: string;
};

/**
 * One layer row as the Map Layers tab shows it: visibility checkbox, zoom,
 * delete and expandable features. Picks the hub or classic variant itself.
 * Callers render it inside their own list wrapper and supply the React key.
 */
export function LayerListItem({
  item,
  onFocusView,
  onOpenDetail,
  defaultExpanded = false,
  tourAttrs,
}: {
  item: LayerWithType;
  /** Opens the panel's focused single-layer view. */
  onFocusView?: () => void;
  /** Opens the layer detail modal (external layers). */
  onOpenDetail?: () => void;
  defaultExpanded?: boolean;
  tourAttrs?: LayerListItemTourAttrs;
}) {
  const hubChrome = useHubChromeEnabled();
  const { focusFeatureOnMap, focusLayerOnMap } = useUscgMapContext();
  const deleteInternalLayer = useDeleteInternalLayerMutation();
  const deleteExternalLayer = useDeleteExternalLayerMutation();

  if (item.type === 'internal') {
    const { layer } = item;
    const zoomToLayer = () => {
      void focusLayerOnMap({
        kind: 'internal',
        layerId: layer.id,
        name: layer.name,
        lastUpdated: layer.updatedAt,
      });
    };
    const internalTour = {
      dataTour: tourAttrs?.dataTour,
      zoomTour: tourAttrs?.zoomTour,
    };
    return hubChrome ? (
      <SitStatInternalLayerItem
        layer={layer}
        defaultOpen={defaultExpanded}
        onFocusView={onFocusView}
        onZoomToLayer={zoomToLayer}
        {...internalTour}
      />
    ) : (
      <InternalLayerItem
        layer={layer}
        defaultExpanded={defaultExpanded}
        onFocusView={onFocusView}
        onDelete={() =>
          deleteInternalLayer.mutate({ internalLayerId: layer.id })
        }
        onZoomToLayer={zoomToLayer}
        {...internalTour}
      />
    );
  }

  const { layer } = item;
  const zoomToLayer = () => {
    void focusLayerOnMap({
      kind: 'external',
      layerId: layer.id,
      name: layer.name,
      lastUpdated: layer.updatedAt,
    });
  };
  const zoomToGraphic = (graphic: __esri.Graphic) => {
    void focusFeatureOnMap({
      graphic,
      layer: { kind: 'external', layerId: layer.id },
    });
  };
  return hubChrome ? (
    <SitStatExternalLayerItem
      layer={layer}
      onOpenInNewClick={onOpenDetail}
      onZoomToLayer={zoomToLayer}
      onZoomToGraphic={zoomToGraphic}
      {...tourAttrs}
    />
  ) : (
    <ExternalLayerItem
      layer={layer}
      onDelete={() => deleteExternalLayer.mutate({ externalLayerId: layer.id })}
      onOpenInNewClick={onOpenDetail}
      onZoomToLayer={zoomToLayer}
      onZoomToGraphic={zoomToGraphic}
      {...tourAttrs}
    />
  );
}
